import { InjectionToken } from '@angular/core';

/**
 * Something that must be dismissed when a session ends.
 *
 * Dialogs, snackbars and other overlays are rendered outside the router outlet, so navigating to the
 * login page does not remove them: the user ends up looking at the login form with a modal from the
 * previous screen still floating on top of it, often one that invites them to take an action they no
 * longer have a session for.
 *
 * `libs/auth` deliberately knows nothing about Angular Material, so each app registers what "close
 * everything" means for it.
 */
export interface AuthSessionUiCleanup {
  closeOverlays(): void;
  /**
   * Stop SignalR (and anything else that bypasses HttpClient) before cookies are cleared.
   * Without this, a dead session still auto-reconnects on the login page: WebSocket 1006,
   * hub negotiate 401, and a follow-up POST /auth/refresh 401.
   */
  stopRealtime?(): void;
}

/**
 * Multi-provider. Every registered cleanup runs, in order, before the session-end navigation.
 *
 * Factories must not list SignalR hubs (or anything that injects ENSURE_FRESH_CREDENTIALS) as
 * `deps`. The terminator is constructed while AuthService is being created; those hubs need
 * AuthService via ENSURE_FRESH_CREDENTIALS, which is NG0200 and a blank app. Look hubs up from
 * Injector inside `stopRealtime()` instead.
 */
export const AUTH_SESSION_UI_CLEANUP = new InjectionToken<AuthSessionUiCleanup[]>(
  'AUTH_SESSION_UI_CLEANUP'
);
